import React, { useEffect, useState } from "react";
import { StatsCard } from "./StatsCard";
import { QrCode, Users, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { dashboardApi } from "../utils/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { MarketingDashboard } from "./MarketingDashboard";

export function Dashboard() {
  const [stats, setStats] = useState<any>({
    total_qr_codes: 0,
    previous_month_qr_codes: 0,
    total_registered_users: 0,
    previous_month_registered_users: 0,
    total_scans_7d: 0,
    previous_7d_scans: 0,
  });
  const [recentScans, setRecentScans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [scansLoading, setScansLoading] = useState(true);

  function getPercentChange(current: number, previous: number) {
    if (previous === 0) {
      if (current === 0) return 0;
      return 100; // Show 100% hike if previous is 0 and current > 0
    }
    return Number((((current - previous) / previous) * 100).toFixed(2));
  }

  function formatTrend(percent: number, current: number, previous: number) {
    if (previous === 0 && current > 0) {
      return "+100% vs last period";
    }
    if (percent > 0) {
      return `+${percent.toFixed(1)}% vs last period`;
    }
    if (percent < 0) {
      return `${percent.toFixed(1)}% vs last period`;
    }
    return "0% vs last period";
  }

  // Fetch dashboard stats (uses shared API client; refreshes token on 401 when possible)
  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      try {
        const result = await dashboardApi.getDashboardCounts();
        if (result?.data) {
          setStats(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch stats", error);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  // Fetch recent scans
  useEffect(() => {
    async function fetchScans() {
      setScansLoading(true);
      try {
        const result = await dashboardApi.getRecentScans();
        if (result?.data?.scans) {
          setRecentScans(result.data.scans);
        }
      } catch (error) {
        console.error("Failed to fetch recent scans", error);
      } finally {
        setScansLoading(false);
      }
    }
    fetchScans();
  }, []);

  // Calculate percentages
  const percentTotalQrCodes = getPercentChange(
    stats.total_qr_codes,
    stats.previous_month_qr_codes
  );
  const percentRegisteredUsers = getPercentChange(
    stats.total_registered_users,
    stats.previous_month_registered_users
  );
  const percentScans7d = getPercentChange(
    stats.total_scans_7d,
    stats.previous_7d_scans
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="mb-2">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome to your management system overview
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">System Overview</TabsTrigger>
          <TabsTrigger value="marketing">Marketing & Acquisition</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-8">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Total QR Codes"
          value={loading ? "..." : stats.total_qr_codes}
          icon={QrCode}
          trend={{
            value: percentTotalQrCodes,
            isPositive: percentTotalQrCodes >= 0,
            display: formatTrend(
              percentTotalQrCodes,
              stats.total_qr_codes,
              stats.previous_month_qr_codes
            ),
          }}
        />
        <StatsCard
          title="Registered Users"
          value={loading ? "..." : stats.total_registered_users}
          icon={Users}
          trend={{
            value: percentRegisteredUsers,
            isPositive: percentRegisteredUsers >= 0,
            display: formatTrend(
              percentRegisteredUsers,
              stats.total_registered_users,
              stats.previous_month_registered_users
            ),
          }}
        />
        <StatsCard
          title="Scans (Last 7 days)"
          value={loading ? "..." : stats.total_scans_7d}
          icon={Activity}
          trend={{
            value: percentScans7d,
            isPositive: percentScans7d >= 0,
            display: formatTrend(
              percentScans7d,
              stats.total_scans_7d,
              stats.previous_7d_scans
            ),
          }}
        />
      </div>

      {/* Recent Scans Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Recent Scans</CardTitle>
        </CardHeader>
        <CardContent>
          {scansLoading ? (
            <div>Loading...</div>
          ) : recentScans.length === 0 ? (
            <div>No recent scans found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>QR Code ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Device</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentScans.map((scan) => (
                  <TableRow key={scan.qrCode?._id || scan._id} className="hover:bg-muted/50">
                    <TableCell>{scan.dateTime}</TableCell>
                    <TableCell className="font-medium">{scan.qrCode?.qrid}</TableCell>
                    <TableCell>{scan.user?.name}</TableCell>
                    <TableCell>
                      {scan.user?.location || (scan.location?.coordinates
                        ? `${scan.location.coordinates[1]}, ${scan.location.coordinates[0]}`
                        : "—")}
                    </TableCell>
                    <TableCell>{scan.device || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </TabsContent>
        <TabsContent value="marketing">
          <MarketingDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
